import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import { Router, NavigationExtras, ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { AlertComponent } from 'ngx-bootstrap/alert/alert.component';
import { SESSION_STORAGE, WebStorageService } from 'ngx-webstorage-service';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';

import { StoreFacade } from '../../../common/facade/StoreFacade';
import { Alert } from '../../../common/util/Alert';
import { ShipmentASN } from '../../../common/model/ShipmentASN';
import { ShipmentASNFacade as ShipmentAsnService } from '../../../common/facade/ShipmentASN.service';
import { Store } from '../../../common/model/Store';
import { TableMessages } from '../../../common/util/TableMessages';
import { Organization } from '../../../common/model/Organization';
import { Strings } from '../../../common/language/strings';
import { res } from '../../../common/language/text/res';
import { ComboMultiSelectComponent } from '../../../containers/combo-multi-select/combo-multi-select.component';
import { ConfigurationInfo } from '../../../common/model/dictionary/ConfigurationInfo';
import { ExcelUtility } from '../../../common/util/ExcelUtility';

@Component({
  selector: 'app-shipments-asn',
  templateUrl: './shipments-asn.component.html',
  styleUrls: ['./shipments-asn.component.css']
})
export class ShipmentsAsnComponent implements OnInit, OnDestroy {
  @ViewChild('detailModal') public detailModal: ModalDirective;
  @ViewChild('cartonGuideModal') public cartonGuideModal: ModalDirective;
  @ViewChild(ComboMultiSelectComponent) cmbMultiSelect: ComboMultiSelectComponent;

  private readonly USE_MOCK_DATA = false;
  
  loading: boolean = false;
  messageLoader: string = '';
  alerts: Alert[] = [];
  storeId: number = -1;
  organizationId: string = null;
  stores: Store[] = [];
  rows: ShipmentASN[] = [];
  hideStores: boolean = false;
  endDate: Date = new Date();
  startDate: Date = new Date();
  today: Date = new Date();
  res = res;
  isSpanish: boolean = true;

  titleASNShipments = res.ASNMerchandiseEntry;
  lblAsnCode = res.ASNCode;
  lblReception = res.Reception;
  lblEntryInventory = res.EntryInventory;
  lblManifest = res.Manifest;
  lblStartDate = res.StartDate;
  lblEndDate = res.EndDate;
  lblSelectStore = res.SelectStore;
  lblSelectAnOption = res.SelectAnOption;
  lblSearch = res.Search;
  lblDownload = res.Download;
  lblClose = res.Close;
  lblDetail = res.DetailCartonGuide;
  lblDelivery = res.Delivery;
  lblStore = res.Store;
  lblView = res.View;

  TenantId: number = 0;
  organizationList: Organization[] = [];
  selectedOrganization: Organization[] = [];
  strOrganizationSelected: string = '';
  botonEnable: boolean = false;
  cartonGuides: string[] = [];

  messagesTable: TableMessages = {
    emptyMessage: '',
    totalMessage: ''
  };

  private destroy$ = new Subject<void>();
  private isUserStore: boolean = false;
  private _filteredData: ShipmentASN[] = [];

  constructor(
    @Inject(SESSION_STORAGE) private _storage: WebStorageService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _service: ShipmentAsnService,
    private _facadeStore: StoreFacade,
    private _datePipe: DatePipe,
    private _excel: ExcelUtility,
    private localeService: BsLocaleService,
    public strings: Strings,
  ) {
    this._storage.set('moduleUrl', this._router.url);
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - ConfigurationInfo.defaultDays);
    this.startDate = defaultDate;
  }

  ngOnInit(){
    this.messageLoader = this.strings.GetText(res.Loading);
    this.isSpanish = this.strings.getLanguageIsSpanish();
    this.localeService.use(this.strings.getLanguage());
    this.messagesTable.emptyMessage = this.strings.GetText(res.ThereIsNoInformation);
    this.messagesTable.totalMessage = 'Total';
    this.TenantId = this.strings.getTenant();
    this.organizationList = this.strings.getAvailableOrganizations().filter(org => org.tenantId === this.TenantId);
    this.validateSession();
    this.loadQueryParams();
    //this.restoreCachedData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subtractTimeFromDate(objDate: Date, intHours: number): Date {
    const numberOfMlSeconds = objDate.getTime();
    const addMlSeconds = intHours * 60 * 60000;
    return new Date(numberOfMlSeconds - addMlSeconds);
  }

  private validateSession(): void {
    const user = this._storage.get('user');
    if (user?.storeId && !isNaN(user.storeId)) {
      this.storeId = Number(user.storeId);
      this.isUserStore = this.storeId !== -1;
      if (this.isUserStore) {
        this.botonEnable = true;
      }
    }
  }

  private loadQueryParams() {
    this._route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['startDate'] && params['endDate']) {
          this.startDate = new Date(params['startDate']);
          this.endDate = new Date(params['endDate']);
        }

        if (params['organizationid']) {
          this.organizationId = params['organizationid'];
          this.strOrganizationSelected = this.organizationId;
          this.selectedOrganization = this.organizationList.filter(org => 
            this.organizationId.includes(org.organizationId)
          );
          this.botonEnable = true;
        }

        if (params['storeid']) {
          this.storeId = Number(params['storeid']);
        }

        if (this.selectedOrganization.length > 0) {
          this.loadStores();
        }
      });
  }

  private loadStores(): void {
    if (!this._service.hasCachedData()) {
      this.clearStoresData();
    }

    if (!this.strOrganizationSelected) {
      this.strOrganizationSelected = this.selectedOrganization
        .map(e => e.organizationId)
        .join(',');
    }

    this.loading = true;
    this._facadeStore.GetParticipateQuantum()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe(
        result => {
          if (result.completed && result.data) {
            this.stores = result.data.filter(store => 
              this.strOrganizationSelected.includes(store.organizationId)
            );
            this.configureStoreAccess();
          } else {
            this.handleLoadStoresError(result.message);
          }
        },
        error => this.handleLoadStoresError(error)
      );

      if (this.storeId !== -1) {
        this.getByDates();
      }
  }

  private clearStoresData(): void {
    this.rows = [];
    this._filteredData = [];
  }

  private configureStoreAccess(): void {
    if (this.isUserStore) {
      this.botonEnable = true;
    } else {
      this.hideStores = false;
      this.storeId = this.storeId !== -1 ? this.storeId : -1;
      
      if (this.storeId !== -1 && !this._service.hasCachedData()) {
        this.botonEnable = true;
        this.getByDates();
      }
    }
  }

  private handleLoadStoresError(error: any): void {
    const errorMsg = error?.message || error || 'Error loading stores';
    this.addAlert(`<strong>Error:</strong> ${errorMsg}`, 'danger');
    this.stores = [];
  }

  addAlert(message: string, type: string): void {
    this.alerts = [];
    this.alerts.push({ type, msg: message });
  }

  onClosed(dismissedAlert: Alert): void {
    this.alerts = this.alerts.filter(alert => alert !== dismissedAlert);
  }

  onChangeOrganization(selectedOrganization: Organization[]): void {
    this.selectedOrganization = selectedOrganization;
    this.strOrganizationSelected = selectedOrganization
      .map(e => e.organizationId)
      .join(',');
      
    this.organizationId = this.strOrganizationSelected;
    this.botonEnable = selectedOrganization.length > 0;
    
    if (selectedOrganization.length > 0) {
      this.loadStores();
    } else {
      this.clearStoresData();
    }
  }

  onChangeStores(): void {
    if (this.storeId !== -1 && this._filteredData.length > 0) {
      this.rows = this._filteredData.filter(item => 
        item.storeId.toString() === this.storeId.toString()
      );
    } else {
      this.rows = [...this._filteredData];
    }
  }

  onChangeStartDate(value: Date): void {
    this.startDate = this.subtractTimeFromDate(value, 6);

    const mes = this.startDate.getMonth();
    this.startDate.setMonth(this.startDate.getMonth() - 1);
    while (this.startDate.getMonth() === mes) {
      this.startDate.setDate(this.startDate.getDate() - 1);
    }

    if (this.startDate > this.endDate) {
      this.addAlert('<strong>!Error¡ ' + this.strings.GetText(res.AlertTheStartDateCannotBeHigher), 'danger');
    }
  }

  onChangeEndDate(value: Date): void {
    this.endDate = this.subtractTimeFromDate(value, 6);

    const mes = this.endDate.getMonth();
    this.endDate.setMonth(this.endDate.getMonth() - 1);
    while (this.endDate.getMonth() === mes) {
      this.endDate.setDate(this.endDate.getDate() - 1);
    }
  }

  getByDates(): void {
    this.loading = true;

    if (new Date(this.startDate) > new Date(this.endDate)) {
      this.addAlert('<strong>Error! </strong>' + this.strings.GetText(res.AlertTheStartDateCannotBeHigher), 'danger');
      this.loading = false;
      return;
    }

    if (this.storeId === -1) {
      this.addAlert(this.strings.GetText(res.AdviceSelectOptionStoreField), 'warning');
      this.loading = false;
      return;
    }

    this._service.GetByDates(
      this.startDate.toDateString(),
      this.endDate.toDateString(),
      this.organizationId,
      this.storeId
    )
    .pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loading = false)
    )
    .subscribe(
      result => {
        if (result.completed) {
          this.rows = result.data;
          this._filteredData = result.data;
        } else {
          this.addAlert('<strong>' + result.message + '</strong>', 'danger');
        }
      },
      error => {
        this.addAlert('<strong>' + error + '</strong>', 'danger');
      }
    );   
  }

  updateFilter(event: KeyboardEvent): void {
    const val = (event.target as HTMLInputElement).value.toLowerCase();
    
    if (!val) {
      this.rows = [...this._filteredData];
      return;
    }

    this.rows = this._filteredData.filter(item => {
      return item.shipmentAsnCode.toLowerCase().includes(val) ||
             
             item.manifest.toLowerCase().includes(val) ||
             item.storeId.toString().includes(val);
    });
  }

  onViewShipmentASNDetail(code: string): void {
    const navigationExtras: NavigationExtras = {
      queryParams: {
        startDate: this.startDate.toDateString(),
        endDate: this.endDate.toDateString(),
        storeid: this.storeId,
        organizationid: this.strOrganizationSelected
      }
    };
    console.log(navigationExtras)
    this._router.navigate(['/inventory/shipments-asn-detail', code], navigationExtras)
    .then((e) => { });
  }

  downloadLayout(): void {
    if (this.rows.length > 0) {
      const data: any = [];
      this.rows.forEach(row => {
        const isMinDate = !row.endShipmentDate || new Date(row.endShipmentDate).getFullYear() <= 1900;
        const info = {
          [this.strings.GetText(res.ASNCode)]: row.shipmentAsnCode,
          [this.strings.GetText(res.Manifest)]: row.manifest,
          [this.strings.GetText(res.Store)]: row.storeId.toString(),
          [this.strings.GetText(res.SendDate)]:
            this.isSpanish
              ? this._datePipe.transform(row.startShipmentDate, 'dd/MM/yy')
              : this._datePipe.transform(row.startShipmentDate, 'MM/dd/yy'),
          [this.strings.GetText(res.ReceivedDate)]: isMinDate ? '' :
            this.isSpanish
              ? this._datePipe.transform(row.endShipmentDate, 'dd/MM/yy')
                  : this._datePipe.transform(row.endShipmentDate, 'MM/dd/yy'),
          ['% ' + this.strings.GetText(res.Received)]: row.percentReception.toString(),
          ['% ' + this.strings.GetText(res.Entry)]: row.percentEntryInventory.toString(),
          [this.strings.GetText(res.Delivery)]: row.deliveryCodes.toString(),
          [this.strings.GetText(res.CartonCode)]: row.cartonGuides ? row.cartonGuides.split(',').map(cg => cg.split('/')[0]?.trim()).join(', ') : '',
          [this.strings.GetText(res.Guide)]: row.cartonGuides ? row.cartonGuides.split(',').map(cg => cg.split('/')[1]?.trim()).join(', ') : ''
        };
        data.push(info);
      });
      this._excel.exportAsExcelFile(data, this.strings.GetText(res.ASNMerchandiseEntry));
    } else {
      this.addAlert(this.strings.GetText(res.AlertSelectActivityFirst), 'danger');
    }
  }

  openCartonModal(cartonGuideCodesArray: string[]) {
    this.cartonGuides = cartonGuideCodesArray;
    if (this.cartonGuides) {
      this.cartonGuideModal.show();
    }
  }

  getCartonGuideCodes(cartonGuides: string): string[] {
    return cartonGuides ? cartonGuides.split(',').map(code => code.trim()) : [];
  }
}
